/** Teams administration and tenant audit log. */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type AuditEvent,
  type Team,
  useAuditEvents,
  useDeleteTeam,
  useSaveTeam,
  useTeams,
} from "@/features/admin/identity/queries";
import { DataTable } from "@/shared/components/data-table";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { TableToolbar } from "@/shared/components/table-toolbar";
import { useListState } from "@/shared/lib/list-state";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { MemberPicker, SitePicker } from "@/shared/components/pickers";
import { formatDateTime } from "@/shared/lib/format";
import { nullable, useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Textarea } from "@/shared/ui/form";
import { Badge } from "@/shared/ui/primitives";

interface TeamValues extends Record<string, unknown> {
  name: string;
  description: string;
  lead_id: string;
  site_id: string;
  is_active: boolean;
}

/** Create / edit team. */
export function TeamDialog({ team, onClose }: { team?: Team; onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const save = useSaveTeam();
  const [members, setMembers] = useState<string[]>(team?.member_ids ?? []);
  const initial = useMemo<TeamValues>(
    () => ({
      name: team?.name ?? "",
      description: team?.description ?? "",
      lead_id: team?.lead_id ?? "",
      site_id: team?.site_id ?? "",
      is_active: team?.is_active ?? true,
    }),
    [team],
  );
  const form = useForm<TeamValues>({
    initial,
    schema: z.object({ name: z.string().trim().min(1, t("app.required")) }),
    idPrefix: "team",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit((v) =>
      save.mutateAsync({
        id: team?.id,
        body: {
          name: v.name.trim(),
          description: v.description,
          lead_id: nullable(v.lead_id),
          member_ids: members,
          site_id: nullable(v.site_id),
          is_active: v.is_active,
        },
      }),
    );
    if (ok) {
      onClose();
    }
  };
  const add = (id: string | null): void => {
    if (id && !members.includes(id)) {
      setMembers([...members, id]);
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={team ? t("teams.edit") : t("teams.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      <Field id="team-name" label={t("app.name")} error={form.errors.name}>
        <Input {...form.field("name")} />
      </Field>
      <Field id="team-description" label={t("app.description")}>
        <Textarea {...form.field("description")} />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="team-lead_id" label={t("teams.lead")}>
          <MemberPicker
            id="team-lead_id"
            value={form.values.lead_id}
            onChange={(id) => form.set("lead_id", id ?? "")}
          />
        </Field>
        <Field id="team-site_id" label={t("app.site")}>
          <SitePicker
            id="team-site_id"
            value={form.values.site_id}
            onChange={(id) => form.set("site_id", id ?? "")}
          />
        </Field>
      </div>
      <Field id="team-members" label={t("teams.members")}>
        <div className="space-y-2">
          <MemberPicker
            id="team-members"
            value=""
            onChange={add}
            placeholder={t("teams.addMember")}
          />
          <div className="flex flex-wrap gap-1">
            {members.map((id) => (
              <Badge key={id} tone="info" className="gap-1">
                {id.slice(0, 8)}…
                <button
                  type="button"
                  aria-label={`${t("app.delete")} ${id.slice(0, 8)}`}
                  onClick={() => setMembers(members.filter((m) => m !== id))}
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        </div>
      </Field>
      <Checkbox
        id="team-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}

/** Teams page. */
export function TeamsPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const teams = useTeams();
  const remove = useDeleteTeam();
  const [dialog, setDialog] = useState<{ team?: Team } | null>(null);
  const manage = can("identity.team.manage");
  const columns: ColumnDef<Team>[] = [
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => <span className="font-medium">{c.row.original.name}</span>,
    },
    {
      id: "description",
      header: t("app.description"),
      cell: (c) => c.row.original.description || "—",
    },
    { id: "members", header: t("teams.members"), cell: (c) => c.row.original.member_ids.length },
    {
      id: "active",
      header: t("app.status"),
      cell: (c) =>
        c.row.original.is_active ? (
          <Badge tone="success">{t("app.active")}</Badge>
        ) : (
          <Badge>{t("app.inactive")}</Badge>
        ),
    },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ team: c.row.original })}
                >
                  {t("app.edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => {
                    if (globalThis.confirm(t("app.confirmDelete"))) {
                      remove.mutate(c.row.original.id);
                    }
                  }}
                >
                  {t("app.delete")}
                </Button>
              </div>
            ),
          } satisfies ColumnDef<Team>,
        ]
      : []),
  ];
  return (
    <div>
      <PageHeader
        title={t("teams.title")}
        actions={manage ? <Button onClick={() => setDialog({})}>{t("teams.new")}</Button> : null}
      />
      {dialog ? <TeamDialog team={dialog.team} onClose={() => setDialog(null)} /> : null}
      {teams.isPending ? (
        <LoadingState />
      ) : teams.isError ? (
        <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
      ) : (
        <CompleteDataTable
          columns={columns}
          data={teams.data}
          getSearchText={(team) => team.name}
          searchDescription={t("tablePilot.searchFields", { fields: t("app.name") })}
          sortFields={[
            { value: "name", label: t("app.name"), valueOf: (team) => team.name },
            {
              value: "members",
              label: t("teams.members"),
              valueOf: (team) => team.member_ids.length,
            },
          ]}
          minWidth={650}
          getRowId={(x) => x.id}
          total={teams.data.length}
        />
      )}
    </div>
  );
}

/** Audit categories offered as filters. */
const AUDIT_CATEGORIES = ["auth", "admin", "access", "data", "workflow"] as const;

/** Audit log page. */
export function AuditLogPage(): ReactElement {
  const { t } = useTranslation();
  const list = useListState(".");
  const category = list.search.kind ?? "";
  const entityType = list.search.entity_type ?? "";
  const paged = useAuditEvents({
    category: category || undefined,
    entity_type: entityType || undefined,
  });
  const columns: ColumnDef<AuditEvent>[] = [
    { id: "at", header: t("app.created"), cell: (c) => formatDateTime(c.row.original.occurred_at) },
    {
      id: "category",
      header: t("audit.category"),
      cell: (c) => <Badge tone="outline">{c.row.original.category}</Badge>,
    },
    {
      id: "action",
      header: t("audit.action"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.action}</span>,
    },
    {
      id: "entity",
      header: t("audit.entity"),
      cell: (c) =>
        c.row.original.entity_type
          ? `${c.row.original.entity_type} ${c.row.original.entity_id?.slice(0, 8) ?? ""}`
          : "—",
    },
    {
      id: "outcome",
      header: t("audit.outcome"),
      cell: (c) => (
        <Badge tone={c.row.original.outcome === "success" ? "success" : "danger"}>
          {c.row.original.outcome}
        </Badge>
      ),
    },
    {
      id: "details",
      header: t("audit.details"),
      cell: (c) => (
        <span className="font-mono text-xs text-muted-foreground">
          {JSON.stringify(c.row.original.details)}
        </span>
      ),
    },
  ];
  return (
    <div>
      <PageHeader title={t("audit.title")} />
      <TableToolbar
        filters={[
          {
            id: "kind",
            label: t("audit.category"),
            kind: "select",
            prominent: true,
            value: category || undefined,
            options: AUDIT_CATEGORIES.map((value) => ({ value, label: value })),
            onChange: (value) =>
              list.update({ kind: typeof value === "string" ? value : undefined }),
            onRemove: () => list.update({ kind: undefined }),
          },
          {
            id: "entity_type",
            label: t("audit.entity"),
            kind: "text",
            value: entityType || undefined,
            onChange: (value) =>
              list.update({ entity_type: typeof value === "string" ? value : undefined }),
            onRemove: () => list.update({ entity_type: undefined }),
          },
        ]}
        onReset={() => list.update({ kind: undefined, entity_type: undefined })}
      />
      {paged.query.isPending ? (
        <LoadingState />
      ) : paged.query.isError ? (
        <ErrorState error={paged.query.error} onRetry={() => void paged.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={paged.items}
          minWidth={950}
          getRowId={(e) => e.id}
          total={paged.total}
          hasMore={paged.hasMore}
          onLoadMore={paged.loadMore}
          isLoadingMore={paged.isLoadingMore}
        />
      )}
    </div>
  );
}
